<!-- modal container -->
<div class="flex flex-col gap-8 p-8 relative w-[800px] max-h-[90vh]">
  <!-- close button -->
  <button
    class="absolute top-4 right-4 flex items-center justify-center size-11 rounded bg-white z-10"
    (click)="onCancel()"
  >
    <fa-icon [icon]="'xmark'" class="text-base"></fa-icon>
  </button>

  <!-- content: header + form -->
  <div class="flex flex-col gap-3 overflow-y-auto min-h-0 -mx-2 px-2">
    <h3 class="text-2xl font-bold pr-12 shrink-0">
      {{ (isEditMode ? 'stream-modal.title-edit' : 'stream-modal.title-add') | translate }}
    </h3>

    <!-- form fields -->
    <div class="flex flex-col gap-6">
    <!-- row: name + type -->
    <div class="flex gap-6">
      <!-- stream name -->
      <div class="flex-1">
        <rk-form-field [showError]="form.get('nomFlux').invalid && form.get('nomFlux').touched">
          <span class="font-bold">{{ 'stream-edit.name' | translate }}</span>
          <input
            class="mt-3"
            [formControl]="form.get('nomFlux')"
            [placeholder]="'stream-modal.name-placeholder' | translate"
          />
        </rk-form-field>
      </div>

      <!-- stream type -->
      <div class="flex-1">
        <span class="font-bold">{{ 'stream-edit.stream-type' | translate }}</span>
        <div class="w-full mt-3">
          <rk-dropdown
            [options]="streamTypeOptions"
            [selectedOption]="currentStreamType"
            [fullWidth]="true"
            (selectionChange)="onStreamTypeChange($event)"
            [placeholder]="'stream-modal.type-placeholder' | translate"
          ></rk-dropdown>
        </div>
      </div>
    </div>

    <!-- stream url (not radioking) -->
    @if (showStreamUrl) {
      <rk-form-field [showError]="form.get('urlFlux').invalid && form.get('urlFlux').touched">
        <span class="font-bold">{{ 'stream-edit.stream-url' | translate }}</span>
        <input
          class="mt-3"
          [formControl]="form.get('urlFlux')"
          placeholder="https://..."
        />
        @if (form.get('urlFlux').errors?.['required'] && form.get('urlFlux').touched) {
          <div class="form-error-message">{{ 'stream-edit.url-required' | translate }}</div>
        }
        @if (form.get('urlFlux').errors?.['invalidUrl'] && form.get('urlFlux').touched) {
          <div class="form-error-message">{{ 'global.url-invalid' | translate }}</div>
        }
        <!-- .m3u8 URLs are reserved to HLS streams -->
        @if (form.get('urlFlux').errors?.['hlsUrlNotAllowed'] && form.get('urlFlux').touched) {
          <div class="form-error-message">{{ 'stream-edit.url-hls-not-allowed' | translate }}</div>
        }
        @if (duplicateStreamName && !form.get('urlFlux').invalid) {
          <div class="text-warning text-xs mt-1">
            <fa-icon icon="exclamation-triangle" class="text-warning mr-1"></fa-icon>
            {{ 'stream-modal.duplicate-url' | translate:{ name: duplicateStreamName } }}
          </div>
        }
      </rk-form-field>
    }

    <!-- user radio dropdown (radioking) -->
    @if (showUserRadio) {
      <div>
        <span class="font-bold">{{ 'stream-edit.radio' | translate }}</span>
        <div class="w-full mt-3">
          <rk-dropdown
            [options]="userRadioDropdownOptions"
            [selectedOption]="currentUserRadio"
            [fullWidth]="true"
            (selectionChange)="onUserRadioChange($event)"
            [placeholder]="'stream-edit.choose-radio' | translate"
          ></rk-dropdown>
          @if (userRadioDropdownOptions.length === 0) {
            <div class="form-error-message mt-1">
              {{ 'stream-edit.radioking-no-radios-error' | translate }}
            </div>
          }
          @if (duplicateStreamName) {
            <div class="text-warning text-xs mt-1">
              <fa-icon icon="exclamation-triangle" class="text-warning mr-1"></fa-icon>
              {{ 'stream-modal.duplicate-url' | translate:{ name: duplicateStreamName } }}
            </div>
          }
        </div>
      </div>
    }

    <!-- title url -->
    @if (showTitleUrl) {
      <rk-form-field [showError]="form.get('urlTitrage').invalid && form.get('urlTitrage').touched">
        <span class="font-bold">{{ 'stream-edit.title-url' | translate }}</span>
        <span class="block text-base opacity-70">
          {{ 'stream-modal.title-url-description' | translate }}
        </span>
        <input
          class="mt-3"
          [formControl]="form.get('urlTitrage')"
          placeholder="https://..."
        />
        @if (form.get('urlTitrage').errors?.['invalidUrl'] && form.get('urlTitrage').touched) {
          <div class="form-error-message">{{ 'global.url-invalid' | translate }}</div>
        }
      </rk-form-field>
    }

    <!-- icecast mount point -->
    @if (showMountPoint) {
      <div>
        <span class="font-bold">{{ 'stream-edit.mount-point' | translate }}</span>
        <div class="w-full mt-3">
          <rk-dropdown
            [options]="icecastMountpoints"
            [fullWidth]="true"
            [selectedOption]="currentIcecastMountPoint"
            (selectionChange)="onMountPointChange($event)"
            [placeholder]="'stream-edit.choose-mountpoint' | translate"
          ></rk-dropdown>
        </div>
      </div>
    }

    <!-- logo upload -->
    <div class="flex flex-col gap-3">
      <span class="font-bold">{{ 'stream-edit.logo' | translate }}</span>
      <rk-image-upload
        (fileSelected)="onImageSelected($event)"
        (imageRemoved)="onRemoveImage()"
        [fetchedImageUrl]="logoUrl"
        [aspectRatio]="'552/248'"
        [iconSize]="32"
        [radius]="37"
        [compactLayout]="true"
      ></rk-image-upload>
      <rk-image-size-guide
        [width]="552"
        [height]="248"
        [lineBreakOption]="'second'"
      ></rk-image-size-guide>
    </div>
    </div>
  </div>

  <!-- footer -->
  <div class="flex items-center justify-end shrink-0">
    <div class="flex gap-4">
      <rk-button
        [variant]="'secondary'"
        [text]="'stream-modal.cancel' | translate"
        (buttonClick)="onCancel()"
      ></rk-button>
      <rk-button
        [variant]="'primary'"
        [text]="(isEditMode ? 'stream-modal.save' : 'stream-modal.add') | translate"
        (buttonClick)="onSubmit()"
      ></rk-button>
    </div>
  </div>
</div>
